import { placeClickToCall } from '../../lib/clickToCall';
import React, { useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import {
  Phone, PhoneIncoming, PhoneOutgoing, RefreshCw, FileText, Voicemail,
  Clock, ChevronDown, ChevronUp, AlertTriangle, Sparkles, Radio,
} from 'lucide-react';
import {
  Badge, cardWrap, btnPrimary, btnSm, emptyMsg, neuShadow, neuShadowInset, OUTCOME_COLORS,
} from './crmShared';

// mm:ss from a seconds count
function fmtDuration(s) {
  const n = Number(s || 0);
  if (!n) return '—';
  const m = Math.floor(n / 60);
  const sec = String(n % 60).padStart(2, '0');
  return `${m}:${sec}`;
}

function fmtWhen(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString(); } catch (_) { return String(ts); }
}

const OUTCOME_LABEL = {
  answered: 'Answered', voicemail: 'Voicemail', no_answer: 'No answer',
  busy: 'Busy', callback_requested: 'Callback requested', ringing: 'Ringing', initiated: 'Initiated',
};

export default function CallSurfaceTab({ contactId, contactData, canUpdate, onReload }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [details, setDetails] = useState({});   // callId → { transcript, recordingSrc, busy, err }
  const [callMsg, setCallMsg] = useState(null);
  const [calling, setCalling] = useState(false);
  const [live, setLive] = useState(null);   // { callId, text, isFinal } — live transcription

  const contactPhone = (contactData && (contactData.phone || contactData.mobile || contactData.phone_number)) || '';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await window.zignagenixAPI.getCRMCallHistory(contactId);
      if (res && res.error) setError(res.message || 'Could not load call history.');
      else setCalls(Array.isArray(res) ? res : []);
    } catch (_) {
      setError('A connection error occurred loading call history.');
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load]);

  // Live transcription (Phase 5g-b live): subscribe to this contact's calls and
  // show the streaming transcript while a call is in progress. No-op server-side
  // unless FEATURE_LIVE_TRANSCRIPTION is on; degrades silently if the socket or
  // token is unavailable.
  useEffect(() => {
    let socket;
    try {
      const token = window.zignagenixAPI && window.zignagenixAPI.getSessionToken && window.zignagenixAPI.getSessionToken();
      if (!token || !contactId) return undefined;
      socket = io(window.location.origin, { auth: { token }, transports: ['websocket', 'polling'], reconnectionAttempts: 5 });
      socket.on('connect', () => socket.emit('crm:call:watch', { contactId }));
      socket.on('crm:call:transcript:live', (p) => {
        if (!p || String(p.contactId) !== String(contactId)) return;
        setLive({ callId: p.callId, text: p.text || '', isFinal: !!p.isFinal });
        if (p.isFinal) { setTimeout(() => { setLive(null); load(); }, 4000); } // settle → fold into history
      });
    } catch (_) { /* realtime optional */ }
    return () => {
      try { if (socket) { socket.emit('crm:call:unwatch', { contactId }); socket.disconnect(); } } catch (_) {}
    };
  }, [contactId, load]);

  const placeCall = useCallback(async () => {
    if (!contactPhone) { setCallMsg({ ok: false, text: 'No phone number on file for this contact.' }); return; }
    setCalling(true);
    setCallMsg(null);
    try {
      /*
       * ⛔ ONE flow for all three Call buttons — src/lib/clickToCall.js. This
       * copy had NO recovery at all: a rep with no callback number saw the raw
       * server error and had nowhere to go, because there is no profile screen
       * in this app to send them to.
       *
       * ⚠ skipConfirm: this button already sits behind its own deliberate
       * "place call" affordance on the call surface, so a second confirm here
       * would be a dialog about a dialog.
       */
      const out = await placeClickToCall({
        contactId, contactPhone, skipConfirm: true,
      });
      setCallMsg({ ok: out.ok, text: out.message });
      if (out.ok) {
        setTimeout(load, 1500);
        if (onReload) onReload();
      }
    } catch (_) {
      setCallMsg({ ok: false, text: 'A connection error occurred starting the call.' });
    } finally {
      setCalling(false);
    }
  }, [contactId, contactPhone, load, onReload]);

  const toggle = useCallback(async (call) => {
    const id = call.id;
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    if (details[id]) return; // cached
    if (!call.has_transcript && !call.ai_summary) return; // nothing to fetch yet
    setDetails((d) => ({ ...d, [id]: { busy: true } }));
    try {
      const res = await window.zignagenixAPI.getCRMCallTranscript(id);
      setDetails((d) => ({
        ...d,
        [id]: { busy: false, transcript: (res && res.transcript) || null, status: res && res.status, err: res && res.error ? (res.message || 'Transcript unavailable.') : null },
      }));
    } catch (_) {
      setDetails((d) => ({ ...d, [id]: { busy: false, err: 'Could not load the transcript.' } }));
    }
  }, [openId, details]);

  const loadRecording = useCallback(async (id) => {
    setDetails((d) => ({ ...d, [id]: { ...(d[id] || {}), recBusy: true, recErr: null } }));
    try {
      const res = await window.zignagenixAPI.getCRMCallRecording(id);
      if (res && res.base64) {
        setDetails((d) => ({ ...d, [id]: { ...(d[id] || {}), recBusy: false, recordingSrc: `data:${res.mime || 'audio/mpeg'};base64,${res.base64}` } }));
      } else {
        setDetails((d) => ({ ...d, [id]: { ...(d[id] || {}), recBusy: false, recErr: (res && res.message) || 'Recording unavailable.' } }));
      }
    } catch (_) {
      setDetails((d) => ({ ...d, [id]: { ...(d[id] || {}), recBusy: false, recErr: 'Could not load the recording.' } }));
    }
  }, []);

  const labelStyle = { fontSize: 11, color: '#6C757D', display: 'flex', alignItems: 'center', gap: 4 };

  return (
    <div style={{ padding: 20 }}>
      {/* Header: click-to-call */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#212529' }}>Calls</div>
          <div style={{ fontSize: 12, color: '#6C757D' }}>{contactPhone ? contactPhone : 'No phone number on file'}</div>
        </div>
        <button
          type="button"
          onClick={placeCall}
          disabled={calling || !contactPhone || canUpdate === false}
          style={{ ...btnPrimary, opacity: (calling || !contactPhone || canUpdate === false) ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Phone size={14} /> {calling ? 'Connecting…' : 'Call'}
        </button>
        <button type="button" onClick={load} style={{ ...btnSm, display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {callMsg && (
        <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 10, fontSize: 13,
          background: callMsg.ok ? '#E8F8EE' : '#FDECEE', color: callMsg.ok ? '#177245' : '#B02A37',
          boxShadow: neuShadowInset }}>
          {callMsg.text}
        </div>
      )}

      {live && (
        <div style={{ marginBottom: 14, padding: '12px 14px', borderRadius: 10, background: '#FFF4F4',
          boxShadow: neuShadowInset, border: '1px solid #F2C7C7' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: '#C0392B', marginBottom: 6 }}>
            <Radio size={13} /> LIVE TRANSCRIPT{live.isFinal ? ' — finalizing…' : ''}
          </div>
          <div style={{ fontSize: 13, color: '#212529', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {live.text || '…'}
          </div>
        </div>
      )}

      {loading && <div style={emptyMsg}>Loading call history…</div>}
      {error && !loading && (
        <div style={{ ...emptyMsg, color: '#B02A37', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <AlertTriangle size={16} /> {error}
        </div>
      )}
      {!loading && !error && calls.length === 0 && (
        <div style={emptyMsg}>No calls logged for this contact yet.</div>
      )}

      {!loading && !error && calls.map((c) => {
        const inbound = c.direction === 'inbound';
        const outColor = OUTCOME_COLORS[c.outcome] || '#6C757D';
        const open = openId === c.id;
        const det = details[c.id] || {};
        return (
          <div key={c.id} style={{ ...cardWrap, padding: 0, marginBottom: 10 }}>
            <div
              role="button"
              onClick={() => toggle(c)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer' }}
            >
              <div style={{ width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: inbound ? '#E0F2FE' : '#E8F8EE', boxShadow: neuShadow, flexShrink: 0 }}>
                {inbound ? <PhoneIncoming size={16} color="#2E8B98" /> : <PhoneOutgoing size={16} color="#177245" />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#212529' }}>
                  {inbound ? 'Inbound' : 'Outbound'} · {c.phone_number || '—'}
                </div>
                <div style={{ ...labelStyle, marginTop: 2 }}>
                  <Clock size={11} /> {fmtWhen(c.called_at)}
                  {c.called_by_name ? ` · ${c.called_by_name}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                {c.outcome && <Badge text={OUTCOME_LABEL[c.outcome] || c.outcome} color={outColor} bg={`${outColor}1A`} />}
                <span style={{ ...labelStyle }}>{fmtDuration(c.duration_seconds)}</span>
                {c.has_recording && <Voicemail size={14} color="#F26522" />}
                {c.has_transcript && <FileText size={14} color="#2E8B98" />}
                {open ? <ChevronUp size={16} color="#ADB5BD" /> : <ChevronDown size={16} color="#ADB5BD" />}
              </div>
            </div>

            {open && (
              <div style={{ padding: '0 16px 16px', borderTop: '1px solid #EEF1F4' }}>
                {c.notes && (
                  <div style={{ fontSize: 12, color: '#495057', marginTop: 12, whiteSpace: 'pre-wrap' }}>{c.notes}</div>
                )}

                {c.ai_summary && (
                  <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 10, background: '#F4F0FF', boxShadow: neuShadowInset }}>
                    <div style={{ ...labelStyle, color: '#6F42C1', fontWeight: 600, marginBottom: 4 }}>
                      <Sparkles size={12} /> AI summary
                    </div>
                    <div style={{ fontSize: 12, color: '#3D2C66' }}>{c.ai_summary}</div>
                  </div>
                )}

                {/* Recording */}
                {c.has_recording && (
                  <div style={{ marginTop: 12 }}>
                    {det.recordingSrc ? (
                      <audio controls src={det.recordingSrc} style={{ width: '100%' }}>
                        <track kind="captions" />
                      </audio>
                    ) : (
                      <button type="button" onClick={() => loadRecording(c.id)} disabled={det.recBusy}
                        style={{ ...btnSm, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Voicemail size={13} /> {det.recBusy ? 'Loading…' : 'Play recording'}
                      </button>
                    )}
                    {det.recErr && <div style={{ fontSize: 12, color: '#B02A37', marginTop: 6 }}>{det.recErr}</div>}
                  </div>
                )}

                {/* Transcript */}
                {(c.has_transcript || det.transcript) && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ ...labelStyle, fontWeight: 600, marginBottom: 4 }}><FileText size={12} /> Transcript</div>
                    {det.busy && <div style={{ fontSize: 12, color: '#ADB5BD' }}>Loading transcript…</div>}
                    {det.err && <div style={{ fontSize: 12, color: '#B02A37' }}>{det.err}</div>}
                    {det.transcript && (
                      <div style={{ fontSize: 12, color: '#495057', whiteSpace: 'pre-wrap', maxHeight: 260, overflowY: 'auto',
                        padding: '10px 12px', borderRadius: 10, boxShadow: neuShadowInset }}>{det.transcript}</div>
                    )}
                  </div>
                )}

                {c.transcript_status && c.transcript_status !== 'done' && !det.transcript && (
                  <div style={{ ...labelStyle, marginTop: 10 }}>Transcript {c.transcript_status}…</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
